import { useCallback } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';

import { FHAntdDateTimePicker, FHAutocomplete, FHSelect, FHTooltip } from '../../../../../../base';
import ClientTimezoneDisplay from '../../../../../ClientTimezoneDisplay';
import { BackgroundTypes } from '../../../../../../../constants';
import { disabledDatesOutsideRange, disabledTimeOutsideRange } from '../../../../../../../helpers';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../../../utils/colors';
import { PARENT_SN_DURATION_OPTIONS } from '../../../../constants';
import { TIME_RANGE_FIELDS } from '../../../../../../../constants';
import {
  normalizeToMinute,
  getClientTimeTooltip,
  getSelectedClientLabel,
  getTimezoneMessage,
} from '../../../utils/general';
import TimeTooltipTitle from './components/timeTooltipTitle';
import ClientOption from './components/clientOption';

import type { Moment } from 'moment';
import type { ChangeEvent, HTMLAttributes } from 'react';
import type { TimeRangeField } from '../../../../../../../constants';
import type {
  IClient,
  IOption,
  IParentSupervision,
  ISelectOption,
  SelectValueType,
} from '../../../../../../../utils/types';

interface SessionControlsProps {
  isSupervisionCompleted: boolean;
  parentSupervision: IParentSupervision | null;
  values: IParentSupervision;
  isEditable: boolean;
  hasPersistedSupervision: boolean;
  showV1: boolean;
  locations: ISelectOption[];
  settings: ISelectOption[];
  clientOptions: IOption[];
  selectedClientDetails: IClient | null;
  timeToolTipOpen: boolean;
  durationToolTipOpen: boolean;
  startBetween10to12: boolean;
  endBetween10to12: boolean;
  zone: string;
  isTZIdentified: boolean;
  abbreviatedZone: string;
  onClientUpdate: (clientId?: number) => void;
  onTimeRangeUpdate: (key: TimeRangeField, value: number) => void;
  onFieldUpdate: <K extends keyof IParentSupervision>(key: K, value: IParentSupervision[K]) => void;
}

function SessionControls({
  isSupervisionCompleted,
  parentSupervision,
  values,
  isEditable,
  hasPersistedSupervision,
  showV1,
  locations,
  settings,
  clientOptions,
  selectedClientDetails,
  timeToolTipOpen,
  durationToolTipOpen,
  startBetween10to12,
  endBetween10to12,
  zone,
  isTZIdentified,
  abbreviatedZone,
  onClientUpdate,
  onTimeRangeUpdate,
  onFieldUpdate,
}: SessionControlsProps) {
  const tooltipTitle = (
    <TimeTooltipTitle
      startBetween10to12={startBetween10to12}
      endBetween10to12={endBetween10to12}
      isTZIdentified={isTZIdentified}
      abbreviatedZone={abbreviatedZone}
    />
  );

  const handleStartDateChange = useCallback(
    (value: Moment) => {
      const normalizedValue = normalizeToMinute(value);
      if (normalizedValue)
        onTimeRangeUpdate(TIME_RANGE_FIELDS.DATE_START, normalizedValue.valueOf());
    },
    [onTimeRangeUpdate],
  );

  const clientTimeTooltip = getClientTimeTooltip(values?.client?.id, selectedClientDetails);

  return (
    <>
      <Grid item xs={12} sm={3} md={2.5} mb={1}>
        <Typography
          fontWeight="bold"
          sx={{ mb: 1, ml: isSupervisionCompleted ? 1 : 0 }}
          textAlign="left"
        >
          Client
        </Typography>
        {isSupervisionCompleted ? (
          <Typography mt={1} ml={1} textAlign="left">
            {parentSupervision?.client?.first_name} {parentSupervision?.client?.last_name}
          </Typography>
        ) : (
          <FHAutocomplete
            data-testid="clientName"
            sx={{ flexGrow: 1, boxShadow: 'none' }}
            inputHeight={40}
            coloredBackground
            placeholder="Select client"
            clearIcon={false}
            disabled={hasPersistedSupervision}
            isOptionEqualToValue={(option: IOption, value: IOption) => option.id === value.id}
            renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => (
              <ClientOption props={props} option={option} />
            )}
            options={clientOptions}
            value={getSelectedClientLabel(clientOptions, values?.client?.id)}
            onChange={(_: ChangeEvent<HTMLSelectElement>, value: IOption | null) => {
              const clientId = value?.id === undefined ? undefined : Number(value.id);
              onClientUpdate(
                clientId !== undefined && Number.isFinite(clientId) ? clientId : undefined,
              );
            }}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={2} md={2} mb={1}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          Start Time
        </Typography>
        {isSupervisionCompleted ? (
          <Typography mt={1} textAlign="left">
            {moment(values?.date_start).format('MM/DD/YY h:mm a')}
          </Typography>
        ) : (
          <FHTooltip
            sx={{ inset: 'auto auto -6px 0px' }}
            title={tooltipTitle}
            arrow
            open={timeToolTipOpen || startBetween10to12}
            placement="top"
            maxwidth={400}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_WHITE}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <span>
              <FHAntdDateTimePicker
                data-testid="startDate"
                format="MM/DD/YYYY h:mm a"
                showTime={{ format: 'h:mm a' }}
                allowClear={false}
                showNow
                style={{ width: '100%', boxShadow: 'none' }}
                inputHeight={40}
                use12Hours
                coloredBackground
                disabled={hasPersistedSupervision && !isEditable}
                disabledDate={(current: Moment) => disabledDatesOutsideRange(current, -3, 0)}
                disabledTime={(current: Moment) => disabledTimeOutsideRange(current, 72)}
                value={values?.date_start ? moment(values.date_start) : null}
                onChange={handleStartDateChange}
                onSelect={handleStartDateChange}
                error={timeToolTipOpen || startBetween10to12}
              />
            </span>
          </FHTooltip>
        )}
      </Grid>
      <Grid item xs={12} sm={3} md={2} mb={1}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }}>
            Client time
          </Typography>
          <FHTooltip
            sx={{ inset: 'auto auto -6px 0px' }}
            title={clientTimeTooltip}
            arrow
            placement="top"
            maxwidth={340}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_WHITE}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <Box sx={{ ml: 0.75 }}>
              <InfoIcon sx={{ color: FORTA_BLUE.primary, width: 21, height: 21 }} />
            </Box>
          </FHTooltip>
        </Box>
        <ClientTimezoneDisplay
          dateTime={values?.date_start ? (isTZIdentified ? moment(values.date_start) : null) : null}
          zone={zone}
          sx={{ backgroundColor: FORTA_BLUE.light3 }}
          message={getTimezoneMessage(values?.client?.id, isTZIdentified)}
          abbreviatedZone={abbreviatedZone}
          selectedClientId={isTZIdentified ? values?.client?.id : null}
        />
      </Grid>
      <Grid item xs={12} sm={3} md={1} mb={1}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          Duration
        </Typography>
        {isSupervisionCompleted ? (
          <Typography mt={1} textAlign="left">
            {
              PARENT_SN_DURATION_OPTIONS.find(
                (option) => option.value === values?.expected_duration,
              )?.label
            }
          </Typography>
        ) : (
          <FHTooltip
            sx={{ inset: 'auto auto -6px 0px' }}
            title={tooltipTitle}
            arrow
            open={durationToolTipOpen || endBetween10to12}
            placement="top"
            maxwidth={400}
            tooltipBorderRadius="8px"
            tooltipbackground={FORTA_WHITE}
            tooltipBoxShadow="0px 0px 6px 2px rgba(18, 78, 120, 0.25)"
          >
            <span style={{ flexGrow: 1 }}>
              <FHSelect
                data-testid="duration"
                sx={{ flexGrow: 1, width: '100%', boxShadow: 'none', borderRadius: '8px' }}
                inputHeight={40}
                backgroundType={BackgroundTypes.Colored}
                disabled={hasPersistedSupervision && !isEditable}
                options={PARENT_SN_DURATION_OPTIONS}
                optionDataTestIdPrefix="duration_option"
                value={values?.expected_duration || ''}
                onUpdate={(value: SelectValueType) => {
                  const selectedDuration = PARENT_SN_DURATION_OPTIONS.find(
                    (option) => option.value === value,
                  );
                  if (selectedDuration) {
                    onTimeRangeUpdate(TIME_RANGE_FIELDS.EXPECTED_DURATION, selectedDuration.value);
                  }
                }}
                error={durationToolTipOpen || endBetween10to12}
              />
            </span>
          </FHTooltip>
        )}
      </Grid>
      <Grid item xs={12} sm={3} md={1.7} mb={1}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          Client location
        </Typography>
        {isSupervisionCompleted ? (
          <Typography mt={1} textAlign="left">
            {locations.find((option) => option.value === values?.location)?.label}
          </Typography>
        ) : (
          <FHSelect
            data-testid="selectLocation"
            sx={{ width: '100%', boxShadow: 'none', borderRadius: '8px' }}
            inputHeight={40}
            backgroundType={BackgroundTypes.Colored}
            disabled={hasPersistedSupervision && !isEditable}
            options={locations}
            optionDataTestIdPrefix="location_option"
            value={values?.location || ''}
            onUpdate={(value: SelectValueType) => {
              const selectedLocation = locations.find((option) => option.value === value);
              if (selectedLocation) onFieldUpdate('location', String(selectedLocation.value));
            }}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={3} md={1.5} mb={1}>
        <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
          {showV1 ? 'Settings' : 'Delivery method'}
        </Typography>
        {isSupervisionCompleted ? (
          <Typography mt={1} textAlign="left">
            {settings.find((option) => option.value === values?.setting)?.label}
          </Typography>
        ) : (
          <FHSelect
            data-testid="deliveryMethodDropdown"
            sx={{ width: '100%', boxShadow: 'none', borderRadius: '8px' }}
            inputHeight={40}
            backgroundType={BackgroundTypes.Colored}
            disabled={hasPersistedSupervision && !isEditable}
            options={settings}
            selectFirstOption={false}
            optionDataTestIdPrefix="setting_option"
            value={values?.setting || ''}
            onUpdate={(value: SelectValueType) => {
              const selectedSetting = settings.find((option) => option.value === value);
              if (selectedSetting) onFieldUpdate('setting', String(selectedSetting.value));
            }}
          />
        )}
      </Grid>
    </>
  );
}

export default SessionControls;
